import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	StoreActionApi,
} from 'react-sweet-state';
import {
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';

export type CommentsContentState = {
	hasContentChanged: boolean;
	isCommentContentReady: boolean;
};

const initialState: CommentsContentState = {
	hasContentChanged: false,
	isCommentContentReady: false,
};

export const getInitialState = (): CommentsContentState => ({
	hasContentChanged: false,
	isCommentContentReady: false,
});

export const actions = {
	onChange:
		(hasChanges: boolean) =>
		({ setState }: StoreActionApi<CommentsContentState>): void => {
			setState({ hasContentChanged: hasChanges });
		},
	resetContentChanged:
		() =>
		({ setState }: StoreActionApi<CommentsContentState>): void => {
			setState({ hasContentChanged: false });
		},
	onCommentContentReady:
		() =>
		({ setState }: StoreActionApi<CommentsContentState>): void => {
			setState({ isCommentContentReady: true });
		},
};

const CommentsContentStore = createStore({
	initialState,
	actions,
	name: 'CommentsContentStore',
});

export const useCommentsContent: HookFunction<
	CommentsContentState,
	BoundActions<
		CommentsContentState,
		{
			onChange: (
				hasChanges: boolean,
			) => ({ setState }: StoreActionApi<CommentsContentState>) => void;
			resetContentChanged: () => ({ setState }: StoreActionApi<CommentsContentState>) => void;
			onCommentContentReady: () => ({ setState }: StoreActionApi<CommentsContentState>) => void;
		}
	>,
	void
> = createHook(CommentsContentStore);
export const useCommentsContentActions: HookActionsFunction<
	BoundActions<
		CommentsContentState,
		{
			onChange: (
				hasChanges: boolean,
			) => ({ setState }: StoreActionApi<CommentsContentState>) => void;
			resetContentChanged: () => ({ setState }: StoreActionApi<CommentsContentState>) => void;
			onCommentContentReady: () => ({ setState }: StoreActionApi<CommentsContentState>) => void;
		}
	>
> = createActionsHook(CommentsContentStore);
export const useCommentsContentState: HookStateFunction<CommentsContentState, void> =
	createStateHook(CommentsContentStore);

/**
 * Reads the current state without subscribing, for event handlers that don't render it
 * (`useCommentsContentState` re-renders its caller on every change).
 */
export const getCommentsContentState = (): CommentsContentState =>
	defaultRegistry.getStore(CommentsContentStore).storeState.getState();
